import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  FaAward,
  FaUsers,
  FaRocket,
  FaChartLine,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaEnvelope,
  FaPhone,
  FaBuilding,
} from "react-icons/fa";
import CountUp from "react-countup";

const AboutSection = styled.section`
  padding: 120px 0;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0a0a0a 100%);
  position: relative;
  overflow: hidden;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(
      circle at 20% 80%,
      rgba(102, 126, 234, 0.03) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 20%,
      rgba(118, 75, 162, 0.03) 0%,
      transparent 50%
    );
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 30px;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 80px;
`;

const SectionBadge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.3);
  padding: 8px 20px;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 20px;
  backdrop-filter: blur(10px);
`;

const SectionTitle = styled(motion.h2)`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #ffffff 0%, #667eea 50%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const SectionSubtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: #b8b8b8;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
  margin-bottom: 80px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 60px;
  }
`;

const ContentLeft = styled.div`
  color: white;
`;

const CompanyInfo = styled(motion.div)`
  margin-bottom: 40px;
`;

const CompanyName = styled.h3`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #ffffff 0%, #667eea 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CompanyDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #b8b8b8;
  margin-bottom: 30px;
`;

const CompanyDetails = styled.div`
  display: grid;
  gap: 20px;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const DetailIcon = styled.div`
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: white;
  flex-shrink: 0;
`;

const DetailContent = styled.div`
  flex: 1;
`;

const DetailLabel = styled.div`
  font-size: 0.9rem;
  color: #b8b8b8;
  margin-bottom: 5px;
`;

const DetailValue = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: #ffffff;
`;

const ContentRight = styled.div`
  position: relative;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
`;

const StatCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 30px;
  text-align: center;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  }
`;

const StatIcon = styled.div`
  font-size: 2.5rem;
  color: #667eea;
  margin-bottom: 15px;
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 8px;
  background: linear-gradient(135deg, #ffffff 0%, #667eea 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const StatLabel = styled.div`
  font-size: 1rem;
  color: #b8b8b8;
`;

const ValuesSection = styled.div`
  margin-top: 80px;
`;

const ValuesTitle = styled(motion.h3)`
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 50px;
  background: linear-gradient(135deg, #ffffff 0%, #667eea 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
`;

const ValueCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 40px;
  text-align: center;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover::before {
    transform: scaleX(1);
  }
`;

const ValueIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: white;
  margin: 0 auto 25px;
`;

const ValueTitle = styled.h4`
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 15px;
`;

const ValueDescription = styled.p`
  color: #b8b8b8;
  line-height: 1.6;
`;

const About = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const values = [
    {
      icon: <FaRocket />,
      title: "创新驱动",
      description: "持续探索最新的数字营销技术和趋势，为客户提供前沿的解决方案",
    },
    {
      icon: <FaUsers />,
      title: "客户至上",
      description: "以客户需求为中心，提供个性化的服务方案，确保客户满意",
    },
    {
      icon: <FaChartLine />,
      title: "数据驱动",
      description: "基于数据分析制定策略，用数据说话，确保营销效果的可衡量性",
    },
    {
      icon: <FaAward />,
      title: "专业品质",
      description: "拥有专业的团队和丰富的经验，为客户提供高质量的服务",
    },
  ];

  return (
    <AboutSection id="about">
      <BackgroundPattern />
      <Container ref={ref}>
        <SectionHeader>
          <SectionBadge
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
          >
            <FaBuilding />
            关于我们
          </SectionBadge>

          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8 }}
          >
            专业团队，值得信赖
          </SectionTitle>

          <SectionSubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            深圳二三三数字科技有限公司，专注于为企业提供专业的数字营销服务
          </SectionSubtitle>
        </SectionHeader>

        <ContentGrid>
          <ContentLeft>
            <CompanyInfo
              initial={{ opacity: 0, x: -50 }}
              animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
              transition={{ duration: 0.8 }}
            >
              <CompanyName>深圳二三三数字科技有限公司</CompanyName>
              <CompanyDescription>
                成立于2014年，我们是一家专注于数字营销领域的专业公司。多年来，我们为超过1000家企业提供了优质的广告投放服务，
                帮助客户实现了平均300%的ROI提升。我们拥有专业的团队和丰富的行业经验，
                致力于为客户提供全方位的数字营销解决方案。
              </CompanyDescription>

              <CompanyDetails>
                <DetailItem>
                  <DetailIcon>
                    <FaMapMarkerAlt />
                  </DetailIcon>
                  <DetailContent>
                    <DetailLabel>公司地址</DetailLabel>
                    <DetailValue>
                      深圳市南山区招商街道蛇口南海大道1031号万海大厦C座201室
                    </DetailValue>
                  </DetailContent>
                </DetailItem>

                <DetailItem>
                  <DetailIcon>
                    <FaCalendarAlt />
                  </DetailIcon>
                  <DetailContent>
                    <DetailLabel>成立时间</DetailLabel>
                    <DetailValue>2014年</DetailValue>
                  </DetailContent>
                </DetailItem>

                <DetailItem>
                  <DetailIcon>
                    <FaEnvelope />
                  </DetailIcon>
                  <DetailContent>
                    <DetailLabel>联系邮箱</DetailLabel>
                    <DetailValue>kun@ersansan.cn</DetailValue>
                  </DetailContent>
                </DetailItem>

                <DetailItem>
                  <DetailIcon>
                    <FaPhone />
                  </DetailIcon>
                  <DetailContent>
                    <DetailLabel>咨询热线</DetailLabel>
                    <DetailValue>400-888-9999</DetailValue>
                  </DetailContent>
                </DetailItem>
              </CompanyDetails>
            </CompanyInfo>
          </ContentLeft>

          <ContentRight>
            <StatsContainer>
              <StatCard
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                whileHover={{ scale: 1.05 }}
              >
                <StatIcon>
                  <FaUsers />
                </StatIcon>
                <StatNumber>
                  <CountUp end={1000} duration={2} />+
                </StatNumber>
                <StatLabel>合作客户</StatLabel>
              </StatCard>

              <StatCard
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                whileHover={{ scale: 1.05 }}
              >
                <StatIcon>
                  <FaRocket />
                </StatIcon>
                <StatNumber>
                  <CountUp end={500} duration={2} />+
                </StatNumber>
                <StatLabel>成功案例</StatLabel>
              </StatCard>

              <StatCard
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                whileHover={{ scale: 1.05 }}
              >
                <StatIcon>
                  <FaChartLine />
                </StatIcon>
                <StatNumber>
                  <CountUp end={300} duration={2} />%
                </StatNumber>
                <StatLabel>平均ROI提升</StatLabel>
              </StatCard>

              <StatCard
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                whileHover={{ scale: 1.05 }}
              >
                <StatIcon>
                  <FaAward />
                </StatIcon>
                <StatNumber>
                  <CountUp end={10} duration={2} />年
                </StatNumber>
                <StatLabel>行业经验</StatLabel>
              </StatCard>
            </StatsContainer>
          </ContentRight>
        </ContentGrid>

        <ValuesSection>
          <ValuesTitle
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            我们的核心价值观
          </ValuesTitle>

          <ValuesGrid>
            {values.map((value, index) => (
              <ValueCard
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                transition={{ duration: 0.8, delay: 0.6 + index * 0.1 }}
                whileHover={{ scale: 1.02, y: -5 }}
              >
                <ValueIcon>{value.icon}</ValueIcon>
                <ValueTitle>{value.title}</ValueTitle>
                <ValueDescription>{value.description}</ValueDescription>
              </ValueCard>
            ))}
          </ValuesGrid>
        </ValuesSection>
      </Container>
    </AboutSection>
  );
};

export default About;
